import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { ApiService, BackOfficeService, MyService, SearchCardTemplateService } from "../../../api";
import { hasPermission, Permissions, translate } from "../../../common/providers";
import {
	CustomButtonLink,
	EmptyState,
	HorizontalDivider,
	I18nTranslate,
	icon,
	SideDrawerMain,
} from "../../../common/components";
import {
	setSelectedProject,
	setFilters,
	setCurrentSearch,
	getTotalResults,
	setTotalMatches,
	search,
	getCriticalityParameters,
	getTypeParameters,
	getNegotiabilityParameters,
	setIsLoadingSearch,
	setIsUpdating,
	setIsFilterTocOut,
	getSortingAlgorithms,
} from "./slice/my-search-template-slice";
import { SearchTemplateResult, SearchTemplateFilters, SearchTemplateDrawer } from "../components";
import { SCORING_SORT_AVAILABLE, VISIBILITY_TEMPLATE } from "../../../common/constants";
import { NavUtils } from "../../../navigation";
import styles from "./MySearchTemplate.module.css";

export default function MySearchTemplate() {
	const [openDrawer, setOpenDrawer] = useState(true);
	const [totalProjects, setTotalProjects] = useState();
	const cancelTokenSourceRef = useRef(null);
	const crossFilterTimeout = null;
	const isDsi = useSelector(({ mySearchTemplate }) => mySearchTemplate.isDsi);
	const cautionBannerDisabled = useSelector(({ context }) => context.cautionBannerHidden);
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const isAdminOrManager = useMemo(
		() => hasPermission({ role: [Permissions.COMPANY_ADMIN, Permissions.COMPANY_MANAGER] }),
		[]
	);
	const initSearch = useCallback(() => {
		if (isDsi) {
			BackOfficeService.getTemplateProjects(cancelTokenSourceRef.current.token, { page: 0, limit: 1 })
				.then((data) => {
					if (Array.isArray(data) && data.length > 0) {
						dispatch(setSelectedProject(data[0]) || null);
					}
				})
				.catch((err) => {
					console.error(err);
				});
		} else {
			const initialProjectId = searchParams.get("projectId");
			if (initialProjectId) {
				MyService.getProjectDetails({ projectId: initialProjectId }, cancelTokenSourceRef.current.token)
					.then((data) => {
						dispatch(setSelectedProject(data));
					})
					.catch((err) => {
						console.error(err);
					});
			} else {
				SearchCardTemplateService.getProjects(cancelTokenSourceRef.current.token, { page: 0, limit: 1 })
					.then((data) => {
						if (Array.isArray(data.contents) && data.contents.length > 0) {
							dispatch(setSelectedProject(data.contents[0]) || null);
						}
						setTotalProjects(data.totalElements);
					})
					.catch((err) => {
						console.error(err);
					});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDsi]);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		if (SCORING_SORT_AVAILABLE) {
			dispatch(getSortingAlgorithms(cancelTokenSourceRef.current.token))
				.then(() => {
					initSearch();
				})
				.catch(console.error);
		} else {
			initSearch();
		}
		return () => {
			clearInterval(crossFilterTimeout);
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initSearch]);
	const handleOpenDrawer = () => setOpenDrawer((prev) => !prev);
	const newProjectButton = (
		<CustomButtonLink color="primary" startIcon={icon.faPlus} to={NavUtils.goToNewProject()} variant="contained">
			{translate("search-templates.no-project-btn")}
		</CustomButtonLink>
	);
	const noProjectRender = (
		<EmptyState
			btn1={isAdminOrManager ? newProjectButton : null}
			content={<I18nTranslate translationKey="search-templates.no-project-text" />}
			title={translate("search-templates.no-project-title")}
		/>
	);
	return (
		<>
			{totalProjects === 0 ? (
				<>{noProjectRender}</>
			) : (
				<SideDrawerMain
					className={styles.container}
					contentClassName={styles.sideDrawer__content}
					data-caution-banner-disabled={cautionBannerDisabled}
					DrawerComponent={SearchTemplateDrawer}
					openDrawer={openDrawer}
					search={search}
					setCurrentSearch={setCurrentSearch}
					setFilters={setFilters}
					setIsLoadingSearch={setIsLoadingSearch}
					setIsUpdating={setIsUpdating}
					slice="mySearchTemplate"
					VISIBILITY_CONSTANTS={VISIBILITY_TEMPLATE}
					onOpenDrawer={handleOpenDrawer}
				>
					<SearchTemplateFilters
						getCriticalityParameters={getCriticalityParameters}
						getNegotiabilityParameters={getNegotiabilityParameters}
						getTypeParameters={getTypeParameters}
						setCurrentSearch={setCurrentSearch}
						setFilters={setFilters}
						setIsLoadingSearch={setIsLoadingSearch}
						setIsUpdating={setIsUpdating}
						slice="mySearchTemplate"
					/>
					<HorizontalDivider />
					<SearchTemplateResult
						getTotalResults={getTotalResults}
						search={search}
						setIsFilterTocOut={setIsFilterTocOut}
						setIsLoadingSearch={setIsLoadingSearch}
						setSelectedProject={setSelectedProject}
						setTotalMatches={setTotalMatches}
						slice="mySearchTemplate"
					/>
				</SideDrawerMain>
			)}
		</>
	);
}
